// Home.jsx
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./Home.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getJwtToken } from "../../utils/auth";
import { FaTools, FaShoppingCart, FaBriefcase, FaBullhorn } from "react-icons/fa";
import { getSearchResults } from "../../services/categoriesApi";
import { unifiedSearch } from "../../services/smartSearchApi";
import AutocompleteSearch from "./AutocompleteSearch";
import { translateToSpanish } from "../../utils/translations";
import { useAuth } from "../../utils/AuthContext";
import { useLocations } from "../../utils/LocationContext"; // Importar el hook de ubicaciones
import { motion, AnimatePresence } from "framer-motion";
import Location from "../Location/Location";
import AdvertisementPopup from "./AdvertisementPopup";
import PartnerPopup from "./PartnerPopup";
import PartnerHomeBox from "./PartnerHomeBox";
import ProviderCategoriesBox from "./ProviderCategoriesBox";
import CreatePostModal from "../Forum/CreatePostModal";
import NewFeaturesModal from "./NewFeaturesModal";
import NewFeaturesBanner from "./NewFeaturesBanner";
import NewBadge from "../NewBadge/NewBadge";

const NEW_FEATURES_RELEASE_KEY = "nf_release_v1";

const Home = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [allSearchResults, setAllSearchResults] = useState({});
    const [filteredResults, setFilteredResults] = useState({});
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    // Estado para IA
    const [useAi, setUseAi] = useState(true);
    const [aiResults, setAiResults] = useState([]);
    const [aiLoading, setAiLoading] = useState(false);
    // Debounce timer
    const [aiTimer, setAiTimer] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { hasJustSignedOut, setHasJustSignedOut } = useAuth();
    const userData = JSON.parse(localStorage.getItem("userData"));

    // Usar el estado global de ubicación
    const { selectedGlobalLocation } = useLocations();

    // Estado local para ubicación
    const [selectedLocation, setSelectedLocation] = useState(selectedGlobalLocation);

    // Estado para abrir/cerrar el modal de creación de post
    const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);

    // Estado de promoción de nuevas features (release)
    const [isNewFeaturesModalOpen, setIsNewFeaturesModalOpen] = useState(false);
    const [showNewFeaturesBanner, setShowNewFeaturesBanner] = useState(false);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(NEW_FEATURES_RELEASE_KEY);
            if (!stored) {
                // Primera visita tras el release: mostrar modal y banner
                setIsNewFeaturesModalOpen(true);
                setShowNewFeaturesBanner(true);
            } else {
                const state = JSON.parse(stored);
                // Si ya vio el modal pero no descartó el banner, lo seguimos mostrando
                if (!state.bannerDismissed) {
                    setShowNewFeaturesBanner(true);
                }
            }
        } catch (e) {
            // Si hay un valor corrupto, lo tratamos como primera visita
            setIsNewFeaturesModalOpen(true);
            setShowNewFeaturesBanner(true);
        }
    }, []);

    const persistNewFeaturesState = (patch) => {
        try {
            const prev = JSON.parse(localStorage.getItem(NEW_FEATURES_RELEASE_KEY) || "{}");
            const next = { ...prev, ...patch };
            localStorage.setItem(NEW_FEATURES_RELEASE_KEY, JSON.stringify(next));
        } catch {
            // ignore
        }
    };

    const handleCloseNewFeaturesModal = () => {
        setIsNewFeaturesModalOpen(false);
        persistNewFeaturesState({ modalSeen: true, modalSeenAt: Date.now() });
    };

    const handleDismissBanner = () => {
        setShowNewFeaturesBanner(false);
        persistNewFeaturesState({ bannerDismissed: true });
    };

    const handleOpenNewFeaturesFromBanner = () => {
        setIsNewFeaturesModalOpen(true);
    };

    const handleOpenCreatePost = async (e) => {
        if (e) e.stopPropagation();

        // Verificar que haya un usuario logueado ANTES de abrir el modal.
        // Si no, redirigir al login y volver a esta página tras autenticarse.
        const token = await getJwtToken();
        if (!token || !userData) {
            toast.info('Debes iniciar sesión para crear un post');
            navigate('/login', {
                state: {
                    from: { pathname: location.pathname, search: location.search },
                    intent: 'createPost'
                }
            });
            return;
        }

        setIsCreatePostOpen(true);
    };

    const handlePostCreated = ({ categoryName } = {}) => {
        setIsCreatePostOpen(false);
        if (categoryName) {
            navigate(`/servicios/${encodeURIComponent(categoryName)}/proveedores?view=posts`);
        }
    };

    // Sincronizar con el estado global cuando cambie
    useEffect(() => {
        if (selectedGlobalLocation) {
            setSelectedLocation(selectedGlobalLocation);
        }
    }, [selectedGlobalLocation]);

    // Callback para el componente Location
    const handleLocationChange = (locationObj) => {
        setSelectedLocation(locationObj);
        // No es necesario actualizar updateGlobalLocation aquí ya que
        // el componente Location ya lo hace internamente
    };

    useEffect(() => {
        if (hasJustSignedOut) {
            setHasJustSignedOut(false);
        }
    }, [hasJustSignedOut, setHasJustSignedOut]);

    useEffect(() => {
        const fetchAllSearchResults = async () => {
            try { // Añadido try...catch para manejar posibles errores en la carga
                const results = await getSearchResults();
                const translatedResults = Object.entries(results).reduce((acc, [key, value]) => {
                    const translatedKey = key.replace(/\b(service|product|products|provider|providers|categories)\b/g, match =>
                        translateToSpanish(match)
                    );
                    const translatedValue = value.replace(/\b(service|product|products|provider|providers|categories)\b/g, match =>
                        translateToSpanish(match)
                    );
                    // *** IMPORTANT: Filter out empty keys here ***
                    if (translatedKey !== "") { // <-- Añadido esta verificación
                        acc[translatedKey] = translatedValue;
                    }
                    return acc;
                }, {});
                setAllSearchResults(translatedResults);
            } catch (error) {
                console.error("Error fetching search results:", error);
                // Opcional: mostrar un toast o mensaje al usuario
                // toast.error("No se pudieron cargar los resultados de búsqueda.");
            }
        };

        fetchAllSearchResults();
    }, []);

    useEffect(() => {
        if (searchQuery.length > 0) {
            const filtered = Object.entries(allSearchResults).reduce((acc, [key, value]) => {
                // Como allSearchResults ya no debería tener claves vacías, esta reducción está bien
                if (key.toLowerCase().includes(searchQuery.toLowerCase())) {
                    acc[key] = value;
                }
                return acc;
            }, {});
            setFilteredResults(filtered);
        } else if (isSearchFocused) {
            // allSearchResults ya está filtrado para no tener claves vacías
            setFilteredResults(allSearchResults);
        } else {
            setFilteredResults({});
        }
    }, [searchQuery, allSearchResults, isSearchFocused]);

    // Efecto para llamar al endpoint de búsqueda semántica cuando useAi está activo
    useEffect(() => {
        // Limpiar timer previo
        if (aiTimer) {
            clearTimeout(aiTimer);
            setAiTimer(null);
        }

        if (!useAi) {
            setAiResults([]);
            setAiLoading(false);
            return;
        }

        if (!searchQuery || searchQuery.trim().length === 0) {
            setAiResults([]);
            setAiLoading(false);
            return;
        }

        // Mostrar loading inmediatamente
        setAiLoading(true);

        // Debounce: esperar 800ms después de teclear para evitar requests innecesarias
        const timer = setTimeout(async () => {
            try {
                const locationParam = selectedLocation?.name || selectedLocation?.code;
                const resp = await unifiedSearch(searchQuery, { location: locationParam, limit: 6 });

                // Debug: loggear la respuesta completa
                console.log('🔍 Respuesta del backend:', resp);

                // Esperamos que resp tenga { results: [...] } o directamente un array
                const resultsArray = Array.isArray(resp) ? resp : (resp.results || []);

                // Filtrar solo resultados con similitud >= 0.6 (60%)
                const filteredResults = resultsArray.filter(item => {
                    const similarity = item.similarity || 0;
                    return similarity >= 0.6;
                });

                console.log('📊 Resultados antes de filtrar:', resultsArray.length);
                console.log('✅ Resultados después de filtrar (>= 60%):', filteredResults.length);
                console.log('📈 Tipos encontrados:', filteredResults.map(r => `${r.type} (${Math.round((r.similarity || 0) * 100)}%)`));

                setAiResults(filteredResults);
                setAiLoading(false);
            } catch (error) {
                console.warn("Error en búsqueda IA:", error);
                setAiResults([]);
                setAiLoading(false);
                // Opcional: desactivar IA después de un fallo para fallback
                // setUseAi(false);
            }
        }, 800); // Debounce de 800ms

        setAiTimer(timer);

        return () => {
            clearTimeout(timer);
        };
    }, [searchQuery, useAi, selectedLocation]);

    // Simplificado: permite navegar sin ubicación.
    const handleResultSelect = (result, url) => {
        setSearchQuery(result);
        setFilteredResults({});
        setIsSearchFocused(false);
        // Si la url es un hash inválido, no navegar
        if (!url || url === "#") return;

        if (result.includes("-")) {
            const [subcategory] = result.split("-").slice(0);
            navigate(url, { state: { subcategory } });
        } else {
            navigate(url);
        }
    };

    const handleCategoryClick = (type) => {
        navigate(`/categorias/${type}`);
    };

    const handleSearchQueryChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            // proceed on Enter if needed or simply prevent default
        }
    };

    const handleSearchFocus = () => {
        setIsSearchFocused(true);
        // Cuando está enfocado, mostrar todos los resultados disponibles (que ya están filtrados para excluir claves vacías)
        setFilteredResults(allSearchResults);
    };

    const handleSearchBlur = () => {
        setTimeout(() => {
            setIsSearchFocused(false);
            if (searchQuery.length === 0) {
                setFilteredResults({});
            }
        }, 200);
    };

    // Variants para animaciones con Framer Motion
    const boxVariants = {
        hidden: { scale: 0, opacity: 0 },
        visible: { scale: 1, opacity: 1 },
    };

    return (
        <div className="home-wrapper">
            <Helmet>
                <title>ContactaMe - Encontrá Servicios y Productos Locales en Argentina</title>
                <meta
                    name="description"
                    content="Plataforma gratuita para encontrar servicios y productos locales en Argentina. Electricistas, plomeros, mecánicos y más. Conectá con profesionales cerca tuyo."
                />
                <meta
                    name="keywords"
                    content="servicios locales, productos Argentina, electricista, plomero, mecánico, servicios profesionales, contactame"
                />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://contactame.com.ar/" />
                <meta property="og:title" content="ContactaMe - Encontrá Servicios y Productos Locales" />
                <meta property="og:description" content="Plataforma gratuita para encontrar servicios y productos locales en Argentina. Conectá con profesionales cerca tuyo." />
                <meta property="og:image" content="https://contactame.com.ar/logo512.png" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://contactame.com.ar/" />
                <meta name="twitter:title" content="ContactaMe - Encontrá Servicios y Productos Locales" />
                <meta name="twitter:description" content="Plataforma gratuita para encontrar servicios y productos locales en Argentina." />
                <meta name="twitter:image" content="https://contactame.com.ar/logo512.png" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://contactame.com.ar/" />

                {/* Schema.org JSON-LD para WebSite con SearchAction */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        "name": "ContactaMe",
                        "url": "https://contactame.com.ar/",
                        "description": "Plataforma para encontrar servicios y productos locales en Argentina",
                        "potentialAction": {
                            "@type": "SearchAction",
                            "target": {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://contactame.com.ar/?q={search_term_string}"
                            },
                            "query-input": "required name=search_term_string"
                        }
                    })}
                </script>

                {/* Schema.org JSON-LD para Organization */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "ContactaMe",
                        "url": "https://contactame.com.ar/",
                        "logo": "https://contactame.com.ar/logo512.png",
                        "description": "Plataforma que conecta proveedores de servicios y productos con clientes en Argentina",
                        "address": {
                            "@type": "PostalAddress",
                            "addressCountry": "AR"
                        }
                    })}
                </script>
            </Helmet>

            {/* No mostramos los popups promocionales mientras el usuario está creando un post:
                aparecían por timer encima del CreatePostModal y, al cerrarlos, el click caía en
                el overlay del modal cerrándolo y perdiendo lo escrito. Reaparecen al cerrar el modal. */}
            {!isCreatePostOpen && <AdvertisementPopup />}
            {!isCreatePostOpen && <PartnerPopup />}
            <Location onLocationChange={handleLocationChange} />
            <div className="search-bar">
                <AutocompleteSearch
                    searchQuery={searchQuery}
                    searchResults={filteredResults}
                    handleSearchQueryChange={handleSearchQueryChange}
                    handleKeyPress={handleKeyPress}
                    handleResultSelect={handleResultSelect}
                    handleSearchFocus={handleSearchFocus}
                    handleSearchBlur={handleSearchBlur}
                    // Props IA - SOLO EN HOME
                    showAiButton={true}
                    useAi={useAi}
                    onToggleAi={(val) => { setUseAi(val); if (!val) { setAiResults([]); setAiLoading(false); } }}
                    aiResults={aiResults}
                    aiLoading={aiLoading}
                />
            </div>

            {showNewFeaturesBanner && (
                <NewFeaturesBanner
                    onDismiss={handleDismissBanner}
                    onOpenDetails={handleOpenNewFeaturesFromBanner}
                />
            )}

            <div className="home-container">
                <AnimatePresence>
                    {(userData?.isProvider === true || userData?.isProvider === "true") && (
                        userData?.providerType === "service" ? (
                            <div className="home-provider-row" key="home-provider-row">
                                <ProviderCategoriesBox userData={userData} />
                                <PartnerHomeBox userData={userData} />
                            </div>
                        ) : (
                            <PartnerHomeBox key="partner-home-box" userData={userData} />
                        )
                    )}
                    <motion.div
                        key="services-box"
                        className={`home-box services-box services-box-split`}
                        variants={boxVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <div
                            className="services-box-action services-action-search"
                            onClick={() => handleCategoryClick("servicios")}
                            role="button"
                            tabIndex={0}
                        >
                            <FaTools className="title-icon" />
                            <h2 className="home-title">
                                <b>SERVICIOS</b>
                            </h2>
                            <p style={{ color: "white", margin: 0 }}>
                                <b>ENCONTRÁ EL CONTACTO QUE ESTÁS BUSCANDO</b>
                            </p>
                        </div>
                        <div className="services-box-divider">
                            <span className="services-box-divider-badge">O</span>
                        </div>
                        <div
                            className="services-box-action services-action-publish"
                            onClick={handleOpenCreatePost}
                            role="button"
                            tabIndex={0}
                        >
                            <NewBadge />
                            <FaBullhorn className="title-icon-sm" />
                            <p className="services-action-publish-title">
                                <b>POSTEÁ LO QUE NECESITÁS</b>
                            </p>
                            <p className="services-action-publish-sub">
                                Y los profesionales te contactan a vos
                            </p>
                        </div>
                        <div className="moving-background"></div>
                    </motion.div>
                    <motion.div
                        key="products-box"
                        className={`home-box products-box products-box-compact`}
                        onClick={() => handleCategoryClick("productos")}
                        variants={boxVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{ filter: "brightness(1.05)", scale: 1.03 }}
                    >
                        <FaShoppingCart className="title-icon" />
                        <h2 className="home-title">
                            <b>PRODUCTOS</b>
                        </h2>
                        <p style={{ color: "white" }}>
                            <b>CONSEGUÍ LOS ARTÍCULOS QUE NECESITA TU HOGAR</b>
                        </p>
                        <div className="moving-background"></div>
                    </motion.div>
                </AnimatePresence>
            </div>
            <CreatePostModal
                isOpen={isCreatePostOpen}
                onClose={() => setIsCreatePostOpen(false)}
                categoryId={null}
                categoryName=""
                onPostCreated={handlePostCreated}
            />

            <NewFeaturesModal
                isOpen={isNewFeaturesModalOpen}
                onClose={handleCloseNewFeaturesModal}
                onExplore={() => {
                    handleCloseNewFeaturesModal();
                    handleOpenCreatePost();
                }}
            />

            {(!userData || !userData.isProvider || userData.isProvider === "false") && (
                <div className="home-container">
                    <motion.div
                        className="home-box prov-box active"
                        onClick={() => navigate("registro/proveedor")}
                        variants={boxVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{ filter: "brightness(0.9)", scale: 1.03 }}
                    >
                        <FaBriefcase className="title-icon" />
                        <h2 className="home-title">
                            <b>OFRECÉ TU SERVICIO O PRODUCTO</b>
                        </h2>
                        <div className="moving-background"></div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default Home;

